import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  ArrowLeft, 
  AlertTriangle, 
  RefreshCw, 
  Edit,
  DollarSign,
  Percent,
  Scale,
  Printer
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);

  useEffect(() => {
    fetchRecipe();
  }, [id]);

  const fetchRecipe = async () => {
    try {
      const response = await axios.get(`${API}/recipes/${id}`, { withCredentials: true });
      setRecipe(response.data);
    } catch (error) {
      toast.error('Failed to load recipe');
      navigate('/recipes');
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const response = await axios.post(
        `${API}/recipes/${id}/recalculate`,
        {},
        { withCredentials: true }
      );
      toast.success('Recipe costs recalculated');
      fetchRecipe();
    } catch (error) {
      toast.error('Failed to recalculate costs');
    } finally {
      setRecalculating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Recipe not found</p>
        <Link to="/recipes">
          <Button variant="link">Back to Recipes</Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="recipe-detail-page">
      {/* === PRINT-ONLY LAYOUT (hidden on screen, shown on print) === */}
      <div className="print-recipe hidden">
        <div className="print-recipe-header" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
          {recipe.image_url && (
            <img src={recipe.image_url} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd', flexShrink: 0 }} />
          )}
          <div>
            <h1 className="print-recipe-title">{recipe.name}</h1>
            {recipe.category && <span className="print-recipe-category">{recipe.category}</span>}
            {recipe.description && <p className="print-recipe-desc">{recipe.description}</p>}
            {recipe.portions > 1 && <p className="print-recipe-portions">Portions: {recipe.portions}</p>}
          </div>
        </div>

        <div className="print-recipe-body">
          <div className="print-ingredients">
            <h2>Ingredients</h2>
            <table className="print-table">
              <thead>
                <tr>
                  <th style={{textAlign:'left'}}>Item</th>
                  <th style={{textAlign:'center'}}>Quantity</th>
                  <th style={{textAlign:'center'}}>Unit</th>
                </tr>
              </thead>
              <tbody>
                {recipe.ingredients?.map((ing, idx) => (
                  <tr key={idx}>
                    <td>{ing.name || ing.stock_item_name || ing.item_name}</td>
                    <td style={{textAlign:'center'}}>{ing.quantity}</td>
                    <td style={{textAlign:'center'}}>{ing.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {recipe.method?.filter(s => s).length > 0 && (
            <div className="print-method">
              <h2>Method</h2>
              <ol>
                {recipe.method.filter(s => s).map((step, idx) => {
                  const text = typeof step === 'string' ? step : step?.text;
                  const mediaUrl = typeof step === 'object' ? step?.media_url : null;
                  const mediaType = typeof step === 'object' ? step?.media_type : null;
                  if (!text && !mediaUrl) return null;
                  return (
                    <li key={idx}>
                      {text}
                      {mediaUrl && mediaType === 'image' && (
                        <img src={mediaUrl} alt={`Step ${idx + 1}`} style={{ display: 'block', maxWidth: 160, maxHeight: 120, objectFit: 'cover', borderRadius: 4, marginTop: 4, border: '1px solid #ddd' }} />
                      )}
                      {mediaUrl && mediaType === 'video' && (
                        <img src={mediaUrl} alt={`Step ${idx + 1} video`} style={{ display: 'block', maxWidth: 160, maxHeight: 120, objectFit: 'cover', borderRadius: 4, marginTop: 4, border: '1px solid #ddd', opacity: 0.85 }} />
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>
          )}
        </div>

        <div className="print-recipe-footer">
          <span className="print-footer-left">Printed: {new Date().toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          <span className="print-footer-center">The Merchant Coffee</span>
          <span className="print-footer-right"></span>
        </div>
      </div>

      {/* === SCREEN LAYOUT (hidden on print) === */}
      <div className="space-y-6 animate-fadeIn screen-only">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/recipes">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {recipe.name}
              </h1>
              {recipe.is_cost_alert && (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Cost Alert
                </Badge>
              )}
            </div>
            {recipe.category && (
              <Badge variant="secondary" className="mt-1">{recipe.category}</Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRecalculate} disabled={recalculating}>
            <RefreshCw className={`w-4 h-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
            Recalculate
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="recipe-print-btn">
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={() => navigate(`/recipes/${recipe.recipe_id}/edit`)} data-testid="recipe-edit-btn">
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Image */}
          {recipe.image_url && (
            <Card>
              <CardContent className="p-0">
                <img
                  src={recipe.image_url}
                  alt={recipe.name}
                  className="w-full h-64 object-cover rounded-lg"
                />
              </CardContent>
            </Card>
          )}

          {/* Description */}
          {recipe.description && (
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{recipe.description}</p>
              </CardContent>
            </Card>
          )}

          {/* Ingredients */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="w-5 h-5" />
                Ingredients
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recipe.ingredients?.length === 0 ? (
                <p className="text-muted-foreground">No ingredients added</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-center">Quantity</TableHead>
                      <TableHead className="text-center">Unit</TableHead>
                      <TableHead className="text-right">Unit Cost</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipe.ingredients?.map((ing, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{ing.name || ing.stock_item_name || ing.item_name}</TableCell>
                        <TableCell className="text-center">{ing.quantity}</TableCell>
                        <TableCell className="text-center">{ing.unit}</TableCell>
                        <TableCell className="text-right">R{(ing.cost_per_unit || ing.cost_price || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-medium">
                          R{(ing.cost || ing.total_cost || 0).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/50">
                      <TableCell colSpan={4} className="font-bold">Total Cost</TableCell>
                      <TableCell className="text-right font-bold">
                        R{recipe.total_cost?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Method */}
          <Card>
            <CardHeader>
              <CardTitle>Method</CardTitle>
            </CardHeader>
            <CardContent>
              {recipe.method?.length === 0 || (recipe.method?.length === 1 && !(typeof recipe.method[0] === 'string' ? recipe.method[0] : recipe.method[0]?.text)) ? (
                <p className="text-muted-foreground">No method steps added</p>
              ) : (
                <ol className="space-y-4">
                  {recipe.method?.map((step, idx) => {
                    const text = typeof step === 'string' ? step : step?.text;
                    const mediaUrl = typeof step === 'object' ? step?.media_url : null;
                    const mediaType = typeof step === 'object' ? step?.media_type : null;
                    if (!text && !mediaUrl) return null;
                    return (
                      <li key={idx} className="flex gap-4">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                          {idx + 1}
                        </span>
                        <div className="space-y-2 pt-1">
                          {text && <p className="text-foreground">{text}</p>}
                          {mediaUrl && mediaType === 'video' && (
                            <video src={mediaUrl} className="rounded-lg max-w-sm border" controls />
                          )}
                          {mediaUrl && mediaType === 'image' && (
                            <img src={mediaUrl} alt={`Step ${idx + 1}`} className="rounded-lg max-w-sm border" />
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Costing */}
        <div className="space-y-6">
          <Card className={recipe.is_cost_alert ? 'border-destructive' : ''}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Costing Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Total Cost</span>
                  <span className="font-bold text-lg">R{recipe.total_cost?.toFixed(2)}</span>
                </div>
                
                <Separator />
                
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Selling (incl VAT)</span>
                  <span className="font-bold text-lg text-accent">
                    R{recipe.selling_price_incl_vat?.toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-sm">Selling (excl VAT)</span>
                  <span className="text-sm font-medium">R{recipe.selling_price_excl_vat?.toFixed(2)}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground text-sm">VAT (15%)</span>
                  <span className="text-sm font-medium">
                    R{((recipe.selling_price_incl_vat || 0) - (recipe.selling_price_excl_vat || 0)).toFixed(2)}
                  </span>
                </div>
                
                <Separator />
                
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Markup</span>
                  <span className="font-medium">{recipe.markup_percentage?.toFixed(1)}%</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cost Percentage Card */}
          <Card className={recipe.is_cost_alert ? 'border-destructive bg-destructive/5' : 'bg-green-50 border-green-200'}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Percent className="w-5 h-5" />
                Cost Percentage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <p className={`text-4xl font-bold ${recipe.is_cost_alert ? 'text-destructive' : 'text-green-600'}`}>
                  {recipe.actual_cost_percentage?.toFixed(1)}%
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Target: {recipe.benchmark_cost_percentage}%
                </p>
                {recipe.is_cost_alert && (
                  <div className="mt-4 p-3 bg-destructive/10 rounded-lg">
                    <p className="text-sm text-destructive flex items-center justify-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Above benchmark by {(recipe.actual_cost_percentage - recipe.benchmark_cost_percentage).toFixed(1)}%
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Consider adjusting selling price or ingredient costs
                    </p>
                  </div>
                )}
                {!recipe.is_cost_alert && (
                  <div className="mt-4 p-3 bg-green-100 rounded-lg">
                    <p className="text-sm text-green-700">
                      Within target cost percentage
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Profit Analysis */}
          <Card>
            <CardHeader>
              <CardTitle>Profit Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Gross Profit (excl VAT)</span>
                  <span className="font-medium text-green-600">
                    R{((recipe.selling_price_excl_vat || 0) - (recipe.total_cost || 0)).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">GP Margin</span>
                  <span className="font-medium">
                    {recipe.selling_price_excl_vat > 0 
                      ? (((recipe.selling_price_excl_vat - recipe.total_cost) / recipe.selling_price_excl_vat) * 100).toFixed(1)
                      : 0}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      {/* end screen-only */}
      </div>
    </div>
  );
};

export default RecipeDetail;
